import type { FC, FormEvent } from 'react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useKlurigoServiceClient } from '../../api'
import UsersIcon from '../../assets/images/users-icon.svg'
import {
  CallToActionCard,
  IconButtonArrowLeft,
  IconButtonArrowRight,
  NicknameTextField,
  Page,
  PageProminentIcon,
  RotatingMessage,
  Stack,
  Typography,
} from '../../components'
import { useAuthContext } from '../../context/auth'
import { useGameContext } from '../../context/game'
import { useUserContext } from '../../context/user'

import { MESSAGES, TITLES } from './text.utils'

const GameJoinPage: FC = () => {
  const navigate = useNavigate()

  const { isUserAuthenticated, revokeGame } = useAuthContext()

  const { currentUser } = useUserContext()

  const { gameID } = useGameContext()

  const { joinGame } = useKlurigoServiceClient()

  const [nickname, setNickname] = useState<string>(
    currentUser?.defaultNickname || '',
  )
  const [nicknameValid, setNicknameValid] = useState<boolean>(false)

  const [isJoiningGame, setIsJoiningGame] = useState(false)

  const handleBack = useCallback(() => {
    void revokeGame({ redirectTo: '/' })
  }, [revokeGame])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (gameID && nickname && nicknameValid) {
      setIsJoiningGame(true)
      joinGame(gameID, nickname)
        .then(() => navigate(`/game`))
        .catch(() => undefined)
        .finally(() => setIsJoiningGame(false))
    }
  }

  return (
    <Page
      layout="contained"
      align="center"
      header={
        <IconButtonArrowLeft
          id="back-button"
          type="button"
          variant="primary"
          intent="accent"
          size="small"
          value="Back"
          onClick={handleBack}
        />
      }
      hideLogin>
      <PageProminentIcon src={UsersIcon} alt="Users" />

      <RotatingMessage
        messages={TITLES}
        renderMessage={(title) => (
          <Typography
            variant="title"
            width="medium"
            align="center"
            color="inverse">
            {title}
          </Typography>
        )}
      />

      <RotatingMessage
        messages={MESSAGES}
        renderMessage={(message) => (
          <Typography
            variant="body"
            width="small"
            align="center"
            color="inverse">
            {message}
          </Typography>
        )}
      />

      {!isUserAuthenticated && (
        <CallToActionCard
          title="Make every game count"
          text="Anonymous play doesn’t keep history. Login to track stats, build quizzes, and host your own live games."
          onClick={() => navigate('/auth/login')}
        />
      )}

      <Stack
        as="form"
        width="content"
        data-testid="join-form"
        aria-label="Join game"
        name="join-game-form"
        onSubmit={handleSubmit}>
        <NicknameTextField
          value={nickname}
          placeholder="Nickname"
          disabled={isJoiningGame}
          onChange={setNickname}
          onValid={setNicknameValid}
        />
        <IconButtonArrowRight
          id="join"
          type="submit"
          variant="primary"
          intent="accent"
          value="OK, Go!"
          loading={isJoiningGame}
          disabled={!nicknameValid}
        />
      </Stack>
    </Page>
  )
}

export default GameJoinPage
